
import {
  ref,
  toRef,
  nextTick,
  PropType,
  computed,
  onMounted,
  defineComponent,
} from 'vue';
import {Rule} from '../../../composables/types';
import useField from '../../../composables/useField';
import Text from '@ohrm/oxd/core/components/Text/Text.vue';

interface TextAreaInputEvent extends InputEvent {
  target: HTMLTextAreaElement;
}

export default defineComponent({
  name: 'oxd-buzz-post-input',
  inheritAttrs: false,
  components: {
    'oxd-text': Text,
  },
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array as PropType<Rule[]>,
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  setup(props, context) {
    const focused = ref(false);
    const textArea = ref<HTMLTextAreaElement>();
    const disabled = toRef(props, 'disabled');
    const modelValue = toRef(props, 'modelValue');

    const initialValue = modelValue.value;

    const onReset = async () => {
      context.emit('update:modelValue', initialValue);
      await nextTick();
    };

    const {hasError, message} = useField({
      fieldLabel: '',
      rules: props.rules,
      modelValue,
      onReset,
      disabled,
    });

    const classes = computed(() => ({
      'oxd-buzz-post': true,
      'oxd-buzz-post--focus': focused.value,
      'oxd-buzz-post--error': hasError.value,
      'oxd-buzz-post--active': !focused.value,
      'oxd-buzz-post--disabled': disabled.value,
      'oxd-buzz-post--composing': modelValue.value,
    }));

    const onFocus = () => {
      focused.value = true;
    };

    const onBlur = () => {
      focused.value = false;
    };

    const calculateTextAreaHeight = () => {
      if (!textArea.value) return;

      // Refactored styles to directly apply since
      // scrollHeight is not calculated properly with
      // explicit heights. first set element height to auto
      // then calculate scrollheight and set element height
      textArea.value.removeAttribute('style');
      textArea.value.style.height = 'auto';

      if (!textArea.value.value) {
        textArea.value.style.height = '30px';
        textArea.value.style.overflow = 'hidden';
        textArea.value.style.whiteSpace = 'nowrap';
        textArea.value.style.textOverflow = 'ellipsis';
      } else if (textArea.value.scrollHeight < 400) {
        textArea.value.style.height = textArea.value.scrollHeight + 'px';
        textArea.value.style.overflowY = 'hidden';
      } else {
        textArea.value.style.height = '400px';
        textArea.value.style.overflowY = 'scroll';
      }
    };

    const onInput = ($e: TextAreaInputEvent) => {
      calculateTextAreaHeight();
      context.emit('update:modelValue', $e.target.value);
    };

    onMounted(async () => {
      await nextTick();
      calculateTextAreaHeight();
    });

    return {
      onBlur,
      classes,
      message,
      onFocus,
      onInput,
      hasError,
      textArea,
    };
  },
});
